import { Dispatch } from "redux";
import { MapDispatchToProps, MapStateToPropsFactory } from "react-redux";

import {
  BetslipBetBuilderMultisDismissNotificationAction,
  UI__BETSLIP_DISMISS_BET_BUILDER_MULTIS_NOTIFICATION_ACTION,
} from "../../store/state/actions";
import {
  getSportsbookBettingCombinations,
  getSportsbookBettingLegs,
} from "@ppb/tbd-store/state/betting/sportsbook-betting/sportsbook-betting-selectors";
import { getSportsbookBettingImplyRunnerFailures } from "../../store/state/sportsbook-betting/sportsbook-betting-selectors";
import { BetslipApplicationState } from "../../store/state/BetslipApplicationState";
import {
  getIsBetBuilderMultisNotificationVisible,
  getSportsbookConfirmationCombinations,
  getIsBetslipLoaded,
} from "../../store/state/betslip-module/betslip-module-selectors";

import { createIsConfirmStep } from "../sportsbook-betslip-confirm-mapper";
import { getMultiBetBuilder, getUnavailableMultiBetBuilderLegIds } from "../../helpers/combinations";
import { i18n } from "../../helpers/i18n";
import { translateMultiple } from "../connected-sportsbook-betslip-mapper";

import {
  createGetBettingEventGroupedLegs,
  createGetConfirmationEventGroupedLegs,
  EventGroupMap,
} from "./multi-bet-builder-card-mapper";
import type { BetslipLocalization, SportsbookBetslipCapabilities } from "../../contract";

export type ContainerProps = {
  shouldFocusStakeField?: boolean;
  localization: BetslipLocalization;
  capabilities: SportsbookBetslipCapabilities;
};

export type CardProps = {
  id: string;
  title: string;
  groups: EventGroupMap;
  isNotificationVisible: boolean;
  hasSelectionConnector: boolean;
  i18n: {
    notification?: string;
  };
};

export type StateProps = CardProps | Record<string, never>;

export type DispatchProps = {
  dispatchBetslipBetBuilderMultisDismissNotification: () => void;
};

const getUnavailableLegIds = (state: BetslipApplicationState, activeLegIds: string[]): string[] => {
  const bettingLegs = getSportsbookBettingLegs(state);
  const runnerFailures = getSportsbookBettingImplyRunnerFailures(state);
  const runners = state.betting?.sportsbookBetting?.runners ?? {};

  return getUnavailableMultiBetBuilderLegIds(activeLegIds, bettingLegs, runners, runnerFailures);
};

export const makeMapStateToProps: MapStateToPropsFactory<StateProps, ContainerProps, BetslipApplicationState> = () => {
  const getIsConfirmStep = createIsConfirmStep();
  const getBettingEventGroupedLegs = createGetBettingEventGroupedLegs();
  const getConfirmationEventGroupedLegs = createGetConfirmationEventGroupedLegs();

  const labels = {
    notification: i18n({ key: "I18N.BETSLIP.SBK.BET_BUILDER_MULTI_ONBOARD" }),
  };

  return (state: BetslipApplicationState, { localization, capabilities }: ContainerProps): StateProps => {
    const isConfirmStep = getIsConfirmStep(state);
    const combination = getMultiBetBuilder(
      isConfirmStep ? getSportsbookConfirmationCombinations(state) : getSportsbookBettingCombinations(state),
    );
    const hasSelectionConnector = capabilities.selectionConnector.enabled;

    if (!getIsBetslipLoaded(state) || !combination) {
      return {};
    }

    const { id, legs = [], betType } = combination;
    const legIds = isConfirmStep ? legs : [...getUnavailableLegIds(state, legs), ...legs];

    return {
      id,
      groups: isConfirmStep
        ? getConfirmationEventGroupedLegs(state, legIds, localization)
        : getBettingEventGroupedLegs(state, legIds, localization),
      title: translateMultiple(betType),
      hasSelectionConnector,
      isNotificationVisible:
        capabilities.multiBetBuilderOnboarding.enabled && getIsBetBuilderMultisNotificationVisible(state),
      i18n: labels,
    };
  };
};

export const mapDispatchToProps: MapDispatchToProps<DispatchProps, ContainerProps> = (dispatch: Dispatch) => ({
  dispatchBetslipBetBuilderMultisDismissNotification: (): void => {
    dispatch<BetslipBetBuilderMultisDismissNotificationAction>({
      type: UI__BETSLIP_DISMISS_BET_BUILDER_MULTIS_NOTIFICATION_ACTION,
    });
  },
});
